import { useCallback, useMemo } from "react";
import { useParams } from "react-router";

import { createLibraryTemplateColumns } from "@/app/library/components/molecules/columns";
import { TemplateDetailDrawer } from "@/app/library/views/templates/components/TemplateDetailDrawer";
import type { CatalogMode } from "@/app/library/utils";
import type { APILibraryTemplateResponseType } from "@/core/data_sources/models/APILibraryTemplate";
import type { APILibraryTemplatePackResponseType } from "@/core/data_sources/models/APILibraryTemplatePack";
import { AppStore } from "@/core/data_sources/store/app";
import { Badge, Button } from "@/core/ui/atoms";
import {
  DataTable,
  PageHeader,
  SummaryRow,
  TableFooter,
  TagList,
} from "@/core/ui/molecules";
import { useStore } from "@/core/utils/hooks";

import { LibraryTemplatePacksStore } from "../data_sources/store";

export function PackDetailPage({ mode }: { readonly mode: CatalogMode }) {
  const params = useParams();
  const packId = params.packId ?? "";
  const { state, hooks } = useStore(LibraryTemplatePacksStore.instance);
  const { actions: appActions } = useStore(AppStore.instance);
  hooks.useTemplatePackDetailService({
    id: packId,
    page: 1,
    page_size: 1,
  });
  const selectedPack = state?.selected?.getData();
  const pack =
    selectedPack && isSelectedPackForRoute(selectedPack, packId)
      ? selectedPack
      : null;
  const templates = pack?.templates ?? [];
  const forms = templates.filter((template) => template.type === "form");
  const media = templates.filter((template) => template.type === "media");
  const models = templates.filter((template) => template.type === "model");
  const isSuperAdmin = mode === "super-admin";
  const packName = pack?.name ?? "Template Pack";
  const verticals =
    pack?.verticals
      ?.map((vertical) => vertical.name ?? vertical.code ?? "")
      .filter(Boolean) ?? [];
  const closeDrawer = useCallback(() => {
    appActions.rightDrawer("close");
  }, [appActions]);
  const openTemplateDrawer = useCallback(
    (template: APILibraryTemplateResponseType) => {
      appActions.rightDrawer(
        "open",
        620,
        <TemplateDetailDrawer onClose={closeDrawer} template={template} />,
      );
    },
    [appActions, closeDrawer],
  );
  const columns = useMemo(
    () => createLibraryTemplateColumns(openTemplateDrawer).slice(0, 5),
    [openTemplateDrawer],
  );

  return (
    <>
      <PageHeader
        actions={
          isSuperAdmin ? (
            <>
              <Button>Edit pack</Button>
              <Button variant="primary">Assign templates</Button>
            </>
          ) : (
            <>
              <Button>View usage</Button>
              <Button
                variant={pack?.status === "active" ? "default" : "primary"}
              >
                {pack?.status === "active"
                  ? "Deactivate pack"
                  : "Activate pack"}
              </Button>
            </>
          )
        }
        breadcrumb={["Library", "Template Packs", packName]}
        subtitle={
          isSuperAdmin
            ? "Manage pack composition before making it available to tenants."
            : undefined
        }
        title={<span className="flex items-center gap-2">{packName}</span>}
      />
      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <section className="rounded-panel border border-border bg-surface p-[18px]">
          <h2 className="mb-2 text-sm font-semibold text-ink">About</h2>
          <p className="text-[13px] leading-6 text-ink-muted">
            {pack?.description}
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Badge dot tone="global">
              {isSuperAdmin ? "Global" : "Library"}
            </Badge>
            <TagList values={verticals} />
            <Badge tone="neutral">{formatPackDate(pack)}</Badge>
          </div>
        </section>
        <section className="rounded-panel border border-border bg-surface p-[18px]">
          <h2 className="mb-2 text-sm font-semibold text-ink">Summary</h2>
          <SummaryRow label="Total templates" value={templates.length} />
          <SummaryRow label="Forms" value={forms.length} />
          <SummaryRow label="Media" value={media.length} />
          <SummaryRow label="Models" value={models.length} />
          <SummaryRow label="Status" value={pack?.status ?? "-"} />
        </section>
      </div>
      <DataTable
        columns={columns}
        footer={<TableFooter count={templates.length} noun="template" />}
        rowKey={(template) =>
          String(template.id ?? template.url ?? template.name ?? "")
        }
        rows={templates}
      />
    </>
  );
}

function isSelectedPackForRoute(
  pack: APILibraryTemplatePackResponseType,
  packId: string,
) {
  return (
    String(pack.id ?? "") === packId ||
    (pack.url?.replace(/\/$/, "").endsWith(`/${packId}`) ?? false)
  );
}

function formatPackDate(pack: APILibraryTemplatePackResponseType | null) {
  return pack?.modified?.split("T")[0] ?? pack?.created?.split("T")[0] ?? "-";
}
